import { useState } from 'react';
import { vehiculesApi } from '../../api/resources';
import { DocumentsJoints } from '../../components/DocumentsJoints.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { FR } from '../../components/ui/FieldRow.jsx';
import { Inp, _inp } from '../../components/ui/Inputs.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { dateSuisse } from '../../lib/dates';
import { tr, trf } from '../../lib/i18n';
import {
  DUREE_LINEAIRE,
  TAUX_DEGRESSIF,
  chargeVehicule,
  tableauAmortissement,
  vehiculesDeLAnnee,
} from '../../lib/vehicules';
import { C } from '../../theme/palette';

/** Les documents qu'on joint à un véhicule. */
const SORTES_DOCUMENT = [
  'Contrat de leasing',
  "Facture d'achat",
  'Permis de circulation',
  'Assurance',
  'Autre',
];

const VIDE = {
  nom: '',
  plaque: '',
  mode: 'LEASING',
  dateDebut: '',
  tauxTva: 8.1,
  prixAchat: '',
  methodeAmortissement: 'LINEAIRE',
  dureeAmortissement: DUREE_LINEAIRE,
  versementInitial: '',
  mensualite: '',
  dureeMois: 48,
  valeurResiduelle: '',
  dateFin: '',
  notes: '',
};

const chf = (n) => DEVISE + ' ' + fmt(n || 0);
const nombre = (v) => (v === '' || v == null ? null : +v);

/** Un choix en deux grandes cases. */
function Deux({ valeur, options, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
      {options.map(([v, libelle, aide]) => (
        <div
          key={v}
          role="radio"
          aria-checked={valeur === v}
          aria-label={libelle}
          onClick={() => onChange(v)}
          style={{
            flex: 1,
            cursor: 'pointer',
            borderRadius: 10,
            padding: '9px 10px',
            border: '2px solid ' + (valeur === v ? C.navy : C.border),
            background: valeur === v ? C.navyXL : C.white,
          }}
        >
          <div style={{ fontSize: 11.5, fontWeight: 800, color: C.ink }}>{tr(libelle)}</div>
          {aide && <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{tr(aide)}</div>}
        </div>
      ))}
    </div>
  );
}

/** Le formulaire d'un véhicule, en leasing ou acheté. */
function Formulaire({ depart, onFini }) {
  const [f, setF] = useState({ ...VIDE, ...depart });
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const champ = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const leasing = f.mode === 'LEASING';

  const enregistre = async () => {
    setErreur('');
    if (!f.nom.trim() || !f.dateDebut) {
      setErreur(tr('Indique le véhicule et la date.'));
      return;
    }
    const fiche = {
      ...f,
      plaque: f.plaque || null,
      tauxTva: nombre(f.tauxTva),
      prixAchat: nombre(f.prixAchat),
      dureeAmortissement: nombre(f.dureeAmortissement),
      versementInitial: nombre(f.versementInitial),
      mensualite: nombre(f.mensualite),
      dureeMois: nombre(f.dureeMois),
      valeurResiduelle: nombre(f.valeurResiduelle),
      dateFin: f.dateFin || null,
      notes: f.notes || null,
    };
    setEnCours(true);
    try {
      const v = f.id ? await vehiculesApi.metAJour(f.id, fiche) : await vehiculesApi.cree(fiche);
      onFini(v);
    } catch (e) {
      setErreur(e?.message || tr('Le véhicule n’a pas pu être enregistré.'));
      setEnCours(false);
    }
  };

  return (
    <Card style={{ marginBottom: 11 }}>
      <Bar
        icon="🚗"
        title={f.id ? tr('Modifier le véhicule') : tr('Nouveau véhicule')}
        color={C.navy}
      />
      <div style={{ padding: 13 }}>
        <Deux
          valeur={f.mode}
          onChange={(mode) => setF((p) => ({ ...p, mode }))}
          options={[
            ['LEASING', '📄 Leasing', 'mensualités + 1er versement'],
            ['ACHAT', '💰 Achat cash', 'amorti chaque année'],
          ]}
        />
        <Inp label="Véhicule (marque, modèle)" value={f.nom} onChange={champ('nom')} />
        <Inp label="Plaque" value={f.plaque || ''} onChange={champ('plaque')} />
        <Inp
          label={leasing ? 'Début du leasing' : "Date d'achat"}
          type="date"
          value={f.dateDebut}
          onChange={champ('dateDebut')}
        />
        {leasing ? (
          <>
            <Inp
              label="1er versement (acompte), TTC"
              type="number"
              value={f.versementInitial}
              onChange={champ('versementInitial')}
            />
            <Inp
              label="Mensualité, TTC"
              type="number"
              value={f.mensualite}
              onChange={champ('mensualite')}
            />
            <Inp
              label="Durée du contrat (mois)"
              type="number"
              value={f.dureeMois}
              onChange={champ('dureeMois')}
            />
            <Inp
              label="Valeur résiduelle (rachat à la fin), TTC"
              type="number"
              value={f.valeurResiduelle}
              onChange={champ('valeurResiduelle')}
            />
          </>
        ) : (
          <>
            <Inp
              label="Prix payé, TTC"
              type="number"
              value={f.prixAchat}
              onChange={champ('prixAchat')}
            />
            <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, marginBottom: 4 }}>
              {tr('AMORTISSEMENT')}
            </div>
            <Deux
              valeur={f.methodeAmortissement}
              onChange={(m) => setF((p) => ({ ...p, methodeAmortissement: m }))}
              options={[
                ['LINEAIRE', '📏 Linéaire', 'le même montant chaque année'],
                [
                  'DEGRESSIF',
                  trf('📉 Dégressif {0} %', TAUX_DEGRESSIF),
                  'plus au début, moins ensuite',
                ],
              ]}
            />
            {f.methodeAmortissement === 'LINEAIRE' && (
              <Inp
                label="Durée (années)"
                type="number"
                value={f.dureeAmortissement}
                onChange={champ('dureeAmortissement')}
              />
            )}
          </>
        )}
        <Inp label="Taux de TVA (%)" type="number" value={f.tauxTva} onChange={champ('tauxTva')} />
        <Inp
          label={leasing ? 'Fin anticipée (si rendu plus tôt)' : 'Vendu le (si vendu)'}
          type="date"
          value={f.dateFin || ''}
          onChange={champ('dateFin')}
        />
        <Inp label="Remarques" value={f.notes || ''} onChange={champ('notes')} />
        {erreur && (
          <div role="alert" style={{ color: C.red, fontSize: 11, marginBottom: 8 }}>
            ✕ {erreur}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <Btn label="Annuler" ghost color={C.grey} full onClick={() => onFini(null)} />
          </div>
          <div style={{ flex: 1 }}>
            <Btn
              label={enCours ? '⏳ Envoi…' : '✓ Enregistrer'}
              color={C.green}
              full
              onClick={enCours ? undefined : enregistre}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}

/** Le détail d'un véhicule : ce qu'il coûte cette année, ses années, ses documents. */
function FicheVehicule({ v, annee, onModifier, onSupprimer }) {
  const c = chargeVehicule(v, annee);
  const leasing = v.mode === 'LEASING';
  const annees = leasing
    ? (() => {
        const debut = +String(v.dateDebut).slice(0, 4);
        const fin = debut + Math.ceil(((+v.dureeMois || 0) + 11) / 12);
        const liste = [];
        for (let a = debut; a <= fin; a += 1) {
          const x = chargeVehicule(v, a);
          if (x.charge > 0) liste.push({ annee: a, charge: x.charge, fin: x.valeurBilan });
        }
        return liste;
      })()
    : tableauAmortissement(v).map((l) => ({
        annee: l.annee,
        charge: l.amortissement,
        fin: l.fin,
      }));
  return (
    <Card style={{ marginBottom: 11 }}>
      <Bar
        icon={leasing ? '📄' : '💰'}
        title={v.nom + (v.plaque ? ' · ' + v.plaque : '')}
        color={leasing ? C.teal : C.purple}
        sub={
          leasing
            ? trf(
                'Leasing dès le {0} · {1} mois · {2} / mois',
                dateSuisse(v.dateDebut),
                v.dureeMois,
                chf(v.mensualite),
              )
            : trf(
                'Acheté le {0} · {1} · {2}',
                dateSuisse(v.dateDebut),
                chf(v.prixAchat),
                v.methodeAmortissement === 'DEGRESSIF'
                  ? trf('dégressif {0} %', TAUX_DEGRESSIF)
                  : trf('linéaire {0} ans', v.dureeAmortissement || DUREE_LINEAIRE),
              )
        }
      />
      <div style={{ padding: 13 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: C.ink, marginBottom: 4 }}>
          {trf('En {0}', annee)}
        </div>
        {leasing ? (
          <>
            <FR
              label={trf('Mensualités ({0} mois, HT)', c.moisPayes || 0)}
              value={chf(c.mensualites)}
            />
            <FR label="1er versement réparti (HT)" value={chf(c.versementReparti)} />
          </>
        ) : (
          <FR label="Amortissement (HT)" value={chf(c.amortissement)} />
        )}
        <FR label="Déductible cette année" value={chf(c.charge)} color={C.green} bold />
        <FR label="TVA récupérable" value={chf(c.tvaRecuperable)} color={C.teal} />
        <FR
          label={
            leasing ? 'Au bilan : 1er versement pas encore réparti' : 'Au bilan : valeur restante'
          }
          value={chf(c.valeurBilan)}
          last
        />
        {annees.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
              <tr style={{ fontSize: 9, color: C.muted, textAlign: 'left' }}>
                <th style={{ padding: '4px 2px' }}>{tr('Année')}</th>
                <th style={{ padding: '4px 2px', textAlign: 'right' }}>{tr('Déductible')}</th>
                <th style={{ padding: '4px 2px', textAlign: 'right' }}>
                  {leasing ? tr("Payé d'avance") : tr('Valeur restante')}
                </th>
              </tr>
            </thead>
            <tbody>
              {annees.map((l) => (
                <tr
                  key={l.annee}
                  style={{
                    fontSize: 10.5,
                    borderTop: '1px solid ' + C.border,
                    fontWeight: l.annee === annee ? 900 : 500,
                    background: l.annee === annee ? C.navyXL : 'transparent',
                  }}
                >
                  <td style={{ padding: '5px 2px' }}>{l.annee}</td>
                  <td style={{ padding: '5px 2px', textAlign: 'right' }}>{fmt(l.charge)}</td>
                  <td style={{ padding: '5px 2px', textAlign: 'right' }}>{fmt(l.fin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <DocumentsJoints genre="VEHICULE" documentId={v.id} sortes={SORTES_DOCUMENT} />
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <div style={{ flex: 1 }}>
            <Btn label="✏️ Modifier" ghost color={C.navy} full onClick={onModifier} />
          </div>
          <div style={{ flex: 1 }}>
            <Btn label="🗑 Supprimer" ghost color={C.red} full onClick={onSupprimer} />
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * Compta → 🚗 Véhicules : leasing et voitures achetées.
 *
 * <p>Un leasing se déduit chaque mois ; son 1er versement (par exemple
 * 10'000) se répartit sur la durée du contrat. Une voiture achetée cash ne se
 * déduit pas d'un coup : elle s'amortit chaque année. La fiche calcule les
 * deux, et garde le contrat, la facture et le permis.
 *
 * @param {object} props propriétés
 * @param {object[]} props.vehicules véhicules
 * @param {(f: (v: object[]) => object[]) => void} props.setVehicules met la liste à jour
 * @param {number} props.annee exercice affiché
 * @returns {import('react').ReactElement} l'onglet
 */
export function VehiculesCompta({ vehicules, setVehicules, annee }) {
  const [edition, setEdition] = useState(null);
  const total = vehiculesDeLAnnee(vehicules, annee);

  const fini = (v) => {
    if (v) {
      setVehicules((liste) =>
        liste.some((x) => x.id === v.id)
          ? liste.map((x) => (x.id === v.id ? v : x))
          : [v, ...liste],
      );
    }
    setEdition(null);
  };

  const supprime = async (v) => {
    if (!window.confirm(trf('Supprimer « {0} » et ses documents ?', v.nom))) return;
    await vehiculesApi.supprime(v.id);
    setVehicules((liste) => liste.filter((x) => x.id !== v.id));
  };

  if (edition) return <Formulaire depart={edition} onFini={fini} />;

  return (
    <div>
      <Card style={{ marginBottom: 11 }}>
        <Bar
          icon="🚗"
          title={tr('Véhicules')}
          color={C.navy}
          sub={trf('Ce qui se déduit en {0}', annee)}
        />
        <div style={{ padding: 13 }}>
          <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
            {tr(
              '📄 Leasing : chaque mensualité se déduit, et le 1er versement est réparti sur toute la durée du contrat.',
            )}
            <br />
            {tr(
              '💰 Achat cash : la voiture ne se déduit pas d’un coup, elle s’amortit chaque année (linéaire 20 % ou dégressif 40 %).',
            )}
            <br />
            {tr('La TVA payée se récupère dans les deux cas.')}
          </div>
          <FR label="Déductible cette année (HT)" value={chf(total.charge)} color={C.green} bold />
          <FR label="TVA récupérable" value={chf(total.tvaRecuperable)} color={C.teal} last />
          <div style={{ marginTop: 10 }}>
            <Btn
              label="+ Ajouter un véhicule"
              color={C.navy}
              full
              onClick={() => setEdition({ ...VIDE })}
            />
          </div>
          {total.avecLeasing && (
            <div style={{ fontSize: 9, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
              {tr(
                'Les paiements du leasing restent dans Dépenses (banque et TVA) ; le bénéfice compte le leasing depuis cette fiche, une seule fois.',
              )}
            </div>
          )}
        </div>
      </Card>
      {(vehicules || []).map((v) => (
        <FicheVehicule
          key={v.id}
          v={v}
          annee={annee}
          onModifier={() => setEdition({ ...VIDE, ...v })}
          onSupprimer={() => supprime(v)}
        />
      ))}
    </div>
  );
}
